import styles from './mood-emoji.module.scss'

type Props = { mood: string | null }

// Slack stores moods as shortcodes (`:slightly_smiling_face:`). Map the
// common ones we expect from the check-in modal to their unicode glyphs.
// Anything not in the table renders as a small dot — the dashboard
// keeps working even if a new shortcode shows up before we add it here.
const SHORTCODE_TO_EMOJI: Record<string, string> = {
  // happy
  smiley: '😀',
  smile: '😄',
  grinning: '😀',
  slightly_smiling_face: '🙂',
  blush: '😊',
  // neutral
  neutral_face: '😐',
  expressionless: '😑',
  no_mouth: '😶',
  // unhappy
  slightly_frowning_face: '🙁',
  frowning: '😦',
  worried: '😟',
  disappointed: '😞',
  pensive: '😔',
  // angry
  angry: '😠',
  rage: '😡',
  // tired / sick
  tired_face: '😫',
  weary: '😩',
  sleeping: '😴',
  yawning_face: '🥱',
  face_with_thermometer: '🤒',
  // thinking
  thinking_face: '🤔',
  confused: '😕',
  // celebrate
  tada: '🎉',
  fire: '🔥',
  muscle: '💪',
  // shorthand the dashboard supports natively (if a future modal sends them)
  great: '🟢',
  good: '🙂',
  ok: '😐',
  meh: '😕',
  bad: '🔴',
}

export function MoodEmoji({ mood }: Props) {
  if (!mood) return <span className={styles.placeholder} aria-hidden />

  const glyph = renderGlyph(mood)
  if (!glyph) return <span className={styles.placeholder} aria-hidden />

  return (
    <span className={styles.emoji} title={`mood: ${mood}`}>
      {glyph}
    </span>
  )
}

function renderGlyph(mood: string): string | null {
  const trimmed = mood.trim()

  // Slack shortcode form: :name:
  const shortcodeMatch = trimmed.match(/^:([a-z0-9_+-]+):$/i)
  if (shortcodeMatch) {
    const name = shortcodeMatch[1]!.toLowerCase()
    return SHORTCODE_TO_EMOJI[name] ?? null
  }

  // Bare keyword form (e.g. "great" / "good" / "bad")
  const keyword = SHORTCODE_TO_EMOJI[trimmed.toLowerCase()]
  if (keyword) return keyword

  // Already a single emoji glyph (any non-ascii codepoint set).
  if (/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27FF}]/u.test(trimmed)) {
    return trimmed
  }

  return null
}
